// Lean, bundle-size-optimized variant of `parseShippingMethodCode` from
// ./service-code.ts, for use in Shopify checkout UI extensions (API 2025-10)
// which enforce a hard 64 KB compiled bundle limit.
//
// The canonical ./service-code.ts is a ~3000-line module whose dynamic-code
// parser validates the carrier-service field against the full carrier/service
// enum imported from ../../fulfillments/fulfillment-carriers-and-services
// (~184 KB) and parses the promised-delivery-date into a Temporal value
// (~143 KB of temporal-polyfill). Importing it from a checkout extension drags
// all of that into the bundle.
//
// Checkout extensions only consume the *parse type discriminator*, the compact
// code's `treatmentId`, and the dynamic/fixed code's `assignedTreatmentIds`
// (see use-assigned-checkout-optimization-treatment / use-checkout-analytics).
// They never read the resolved carrier service, cost breakdown, ObjectId
// validity, or promised delivery date. This module therefore reproduces the
// exact JSON shape validation and parse-attempt ORDER of the canonical parser
// while DELIBERATELY relaxing the parts the extension does not use:
//
//   * carrier-service / cost-breakdown fields are accepted as opaque (not
//     validated against the carrier enum, not mapped) — so no fulfillment
//     tables are pulled.
//   * ObjectId-shaped fields are validated as plain strings (no @redotech/rpc
//     ObjectId refinement).
//   * promised-delivery-date IS parsed into Temporal (parity with canonical):
//     this is the one piece of dynamic/compact parse surface a checkout
//     extension consumes beyond treatment ids — the fulfillment-cutoff widget
//     needs the soonest promised delivery date. Temporal is already in the
//     extension bundle (temporal-polyfill/global), so mirroring the canonical
//     `parsePromisedDeliveryDate` here adds no heavy dependency; it does NOT
//     pull the fulfillment/carrier barrels.
//
// CONSEQUENCE: a malformed carrier-service object that the canonical parser
// would reject will still "parse" here. That is acceptable for treatment-id
// attribution but means this file MUST be kept in sync with the parse-order
// and key semantics of ./service-code.ts. service-code-lean.spec.ts asserts
// parity against the canonical parser for representative inputs.

// Hand-written shape guards replacing the former zod schemas. zod is the
// single largest removable dependency in the 64 KB-capped 2025-10 checkout
// bundle (~110 KB raw); this module was its last entry point there. Each
// guard reproduces the exact behavior of the zod construct it replaces:
//   isObj           = z.object(...)            (non-null, non-array object;
//                                               unknown keys ignored)
//   isStr           = z.string()
//   isOptStr        = z.string().optional()    (undefined OR string; a
//                                               present null is invalid,
//                                               matching zod .optional())
//   isOptBool       = z.boolean().optional()
//   isOptNonNegInt  = z.number().int().nonnegative().optional()
//   isOptStrArray   = z.array(z.string()).optional()
// JSON.parse never yields `undefined` values, so a missing key reads as
// `undefined` exactly like an absent optional. service-code-lean.spec.ts
// asserts parity against the canonical zod parser.
function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
function isStr(v: unknown): v is string {
  return typeof v === "string";
}
function isOptStr(v: unknown): v is string | undefined {
  return v === undefined || typeof v === "string";
}
function isOptBool(v: unknown): v is boolean | undefined {
  return v === undefined || typeof v === "boolean";
}
function isOptNonNegInt(v: unknown): v is number | undefined {
  return (
    v === undefined || (typeof v === "number" && Number.isInteger(v) && v >= 0)
  );
}
export const MAX_CURRENCY_DECIMALS = 3;
function isOptCurrencyDecimals(v: unknown): v is number | undefined {
  return (
    v === undefined ||
    (typeof v === "number" &&
      Number.isInteger(v) &&
      v >= 0 &&
      v <= MAX_CURRENCY_DECIMALS)
  );
}
function isOptStrArray(v: unknown): v is string[] | undefined {
  return (
    v === undefined ||
    (Array.isArray(v) && v.every((x) => typeof x === "string"))
  );
}

export interface PromisedDeliveryDate {
  /**
   * UTC epoch milliseconds of the promised delivery instant. The canonical
   * parser keeps a `Temporal.ZonedDateTime`; checkout extensions only need to
   * order delivery dates and render a date label, so we keep the instant as a
   * plain number and avoid pulling `temporal-polyfill` (~119 KB raw) into the
   * 64 KB-capped bundle. Equals `canonical.date.epochMilliseconds`
   * (asserted by service-code-lean.spec.ts).
   */
  epochMs: number;
  visibleToCustomer: boolean;
}

/**
 * Interpret a wall-clock time as occurring in the given IANA time zone and
 * return its UTC epoch ms — the Temporal-free equivalent of
 * `Temporal.PlainDateTime.from(...).toZonedDateTime(timeZone)`. Uses the
 * standard single-pass `Intl.DateTimeFormat` offset technique.
 */
function zonedWallTimeToEpochMs(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): number {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(utcGuess));
  const p: Record<string, string> = {};
  for (const part of parts) {
    p[part.type] = part.value;
  }
  const asUtc = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    Number(p.hour),
    Number(p.minute),
    Number(p.second),
  );
  const offsetMs = asUtc - utcGuess;
  return utcGuess - offsetMs;
}

/**
 * Temporal-free equivalent of `parsePromisedDeliveryDate` in
 * ./service-code.ts. Produces the same UTC instant the canonical parser
 * produces (`canonical.date.epochMilliseconds`), asserted by
 * service-code-lean.spec.ts. Handles forms:
 * - "1.20250616" (from before 06/18/25) -> date at 00:00 UTC
 * - "1.20250627T12:37:30.65806:00[America/Denver]" (06/18/25–06/23/25) ->
 *   date part only, 00:00 UTC (mirrors canonical's non-matching branch)
 * - "1.20250627T12:37[UTC]" (after 06/23/25) -> wall time in the named zone
 */
export function parsePromisedDeliveryDate(
  serializedPromisedDeliveryDate: string,
): PromisedDeliveryDate {
  const [visibleToCustomer, date] = serializedPromisedDeliveryDate.split(
    ".",
    2,
  );

  const match = date?.match(/^(\d{8})T(\d{2}:\d{2})\[(.+)\]$/);
  if (match) {
    const [, datePart, timePart, timeZone] = match;
    const y = Number(datePart!.slice(0, 4));
    const mo = Number(datePart!.slice(4, 6));
    const d = Number(datePart!.slice(6, 8));
    const [hh, mm] = timePart!.split(":").map(Number);
    return {
      visibleToCustomer: visibleToCustomer === "1",
      epochMs: zonedWallTimeToEpochMs(y, mo, d, hh!, mm!, timeZone!),
    };
  }

  const dateOnly = date!.includes("T") ? date!.split("T")[0]! : date!;
  // `Temporal.PlainDate.from(dateOnly).toZonedDateTime("UTC")` == that date at
  // 00:00 UTC. `dateOnly` is either "YYYY-MM-DD" or compact "YYYYMMDD".
  const iso = dateOnly.includes("-")
    ? dateOnly
    : `${dateOnly.slice(0, 4)}-${dateOnly.slice(4, 6)}-${dateOnly.slice(6, 8)}`;
  const [yy, mmo, dd] = iso.split("-").map(Number);
  return {
    visibleToCustomer: visibleToCustomer === "1",
    epochMs: Date.UTC(yy!, mmo! - 1, dd!),
  };
}

// Source of truth for the service-code key constants + compact-code key enum
// shared with the canonical ./service-code.ts. They live here (the dependency-
// free module) rather than in ./service-code.ts so the checkout bundle never
// has to import the heavy canonical module to get them. ./service-code.ts
// imports these from here. DO NOT ADD A KEY "d" (it identifies compact codes).
export const PROVIDER_KEY = "p";
export const RATE_TABLE_ID_KEY = "r";
export const CHECKOUT_TREE_NODE_ID_KEY = "n";
export const ASSIGNED_TREATMENT_IDS_KEY = "t";
export const CARRIER_SERVICE_KEY = "cs";
export const COST_BREAKDOWN_KEY = "cb";
export const PROMISED_DELIVERY_DATE_IN_YYYYMMDD_KEY = "dd";

export enum CompactServiceCodeKey {
  PROVIDER = "p",
  DOCUMENT_ID = "d",
  RATE_TABLE_ID = "r",
  NODE_ID = "n",
  TREATMENT_ID = "t",
  PROMISED_DELIVERY_DATE = "dd",
  CARRIER_SERVICE_KEY = "csm",
  PREVIEW_MODE = "pm",
  REDO_COVERAGE = "rc",
  SHIPPING_COST = "s",
  LANDED_COST = "l",
  SHOP_DECIMALS = "sd",
}

export interface CompactServiceCodeParsedInfo {
  documentId: string;
  rateTableId: string;
  nodeId: string;
  treatmentId?: string;
  promisedDeliveryDate?: PromisedDeliveryDate;
  shippingCost?: number;
  landedCost?: number;
  shopDecimals?: number;
}

// Shape guard equivalent to the former `compactServiceCodeSchema`
// (z.object).safeParse: returns the validated record or null. Unknown keys
// are ignored (zod object is non-strict).
function validateCompactServiceCode(
  data: unknown,
): {
  d: string;
  r: string;
  n: string;
  t?: string;
  dd?: string;
  s?: number;
  l?: number;
  sd?: number;
} | null {
  if (
    !isObj(data) ||
    data[CompactServiceCodeKey.PROVIDER] !== "redo" ||
    !isStr(data[CompactServiceCodeKey.DOCUMENT_ID]) ||
    !isStr(data[CompactServiceCodeKey.RATE_TABLE_ID]) ||
    !isStr(data[CompactServiceCodeKey.NODE_ID]) ||
    !isOptStr(data[CompactServiceCodeKey.TREATMENT_ID]) ||
    !isOptStr(data[CompactServiceCodeKey.PROMISED_DELIVERY_DATE]) ||
    !isOptStr(data[CompactServiceCodeKey.CARRIER_SERVICE_KEY]) ||
    !isOptBool(data[CompactServiceCodeKey.PREVIEW_MODE]) ||
    !isOptBool(data[CompactServiceCodeKey.REDO_COVERAGE]) ||
    !isOptNonNegInt(data[CompactServiceCodeKey.SHIPPING_COST]) ||
    !isOptNonNegInt(data[CompactServiceCodeKey.LANDED_COST]) ||
    !isOptCurrencyDecimals(data[CompactServiceCodeKey.SHOP_DECIMALS])
  ) {
    return null;
  }
  return {
    d: data[CompactServiceCodeKey.DOCUMENT_ID] as string,
    r: data[CompactServiceCodeKey.RATE_TABLE_ID] as string,
    n: data[CompactServiceCodeKey.NODE_ID] as string,
    t: data[CompactServiceCodeKey.TREATMENT_ID] as string | undefined,
    dd: data[CompactServiceCodeKey.PROMISED_DELIVERY_DATE] as
      | string
      | undefined,
    s: data[CompactServiceCodeKey.SHIPPING_COST] as number | undefined,
    l: data[CompactServiceCodeKey.LANDED_COST] as number | undefined,
    sd: data[CompactServiceCodeKey.SHOP_DECIMALS] as number | undefined,
  };
}

function parseCompactServiceCode(
  serviceCode: string,
): CompactServiceCodeParsedInfo | null {
  try {
    const data = validateCompactServiceCode(JSON.parse(serviceCode));
    if (!data) {
      return null;
    }
    const promisedDeliveryDate = data.dd
      ? parsePromisedDeliveryDate(data.dd)
      : undefined;
    return {
      documentId: data.d,
      rateTableId: data.r,
      nodeId: data.n,
      treatmentId: data.t,
      promisedDeliveryDate,
      shippingCost: data.s,
      landedCost: data.l,
      shopDecimals: data.sd,
    };
  } catch {
    return null;
  }
}

// Shape guard equivalent to the former `RedoDynamicRateServiceCodeSchema`
// (z.object).parse. `cs`/`cb` are required-present but opaque (the canonical
// parser validates them against the carrier enum; here they only act as the
// load-bearing discriminator that keeps a fixed/compact code from being
// misclassified as dynamic — parse order matters). z.record(z.string(),
// z.unknown()) ≡ "is a non-null, non-array object".
function validateDynamicServiceCode(
  data: unknown,
): { r?: string; t?: string[]; dd?: string } | null {
  if (
    !isObj(data) ||
    data[PROVIDER_KEY] !== "redo" ||
    !isOptStr(data[RATE_TABLE_ID_KEY]) ||
    !isStr(data[CHECKOUT_TREE_NODE_ID_KEY]) ||
    !isObj(data[CARRIER_SERVICE_KEY]) ||
    !isObj(data[COST_BREAKDOWN_KEY]) ||
    !isOptStrArray(data[ASSIGNED_TREATMENT_IDS_KEY]) ||
    !isOptStr(data[PROMISED_DELIVERY_DATE_IN_YYYYMMDD_KEY])
  ) {
    return null;
  }
  return {
    r: data[RATE_TABLE_ID_KEY] as string | undefined,
    t: data[ASSIGNED_TREATMENT_IDS_KEY] as string[] | undefined,
    dd: data[PROMISED_DELIVERY_DATE_IN_YYYYMMDD_KEY] as string | undefined,
  };
}

function parseDynamicShippingMethodCode(
  code: string,
): {
  provider: "redo";
  rateTableId?: string | undefined;
  assignedTreatmentIds?: string[] | undefined;
  promisedDeliveryDate?: PromisedDeliveryDate | undefined;
} | null {
  try {
    const redoServiceCode = validateDynamicServiceCode(JSON.parse(code));
    if (!redoServiceCode) {
      return null;
    }
    const promisedDeliveryDate = redoServiceCode.dd
      ? parsePromisedDeliveryDate(redoServiceCode.dd)
      : undefined;
    return {
      provider: "redo",
      rateTableId: redoServiceCode.r,
      assignedTreatmentIds: redoServiceCode.t,
      promisedDeliveryDate,
    };
  } catch {
    return null;
  }
}

// Shape guard equivalent to the former `RedoFlatRateServiceCodeSchema`
// (z.object).parse.
function validateFixedServiceCode(
  data: unknown,
): { r: string; n: string; t?: string[]; d?: string } | null {
  if (
    !isObj(data) ||
    data[PROVIDER_KEY] !== "redo" ||
    !isStr(data[RATE_TABLE_ID_KEY]) ||
    !isStr(data[CHECKOUT_TREE_NODE_ID_KEY]) ||
    !isOptStrArray(data[ASSIGNED_TREATMENT_IDS_KEY]) ||
    !isOptStr(data[CompactServiceCodeKey.DOCUMENT_ID]) ||
    !isOptBool(data[CompactServiceCodeKey.PREVIEW_MODE]) ||
    !isOptBool(data[CompactServiceCodeKey.REDO_COVERAGE])
  ) {
    return null;
  }
  return {
    r: data[RATE_TABLE_ID_KEY] as string,
    n: data[CHECKOUT_TREE_NODE_ID_KEY] as string,
    t: data[ASSIGNED_TREATMENT_IDS_KEY] as string[] | undefined,
    d: data[CompactServiceCodeKey.DOCUMENT_ID] as string | undefined,
  };
}

function parseFixedShippingMethodCode(
  code: string,
): {
  provider: "redo";
  rateTableId: string;
  assignedTreatmentIds?: string[] | undefined;
  checkoutTreeNodeId: string;
  documentId: string | undefined;
} | null {
  try {
    const redoServiceCode = validateFixedServiceCode(JSON.parse(code));
    if (!redoServiceCode) {
      return null;
    }
    return {
      provider: "redo",
      rateTableId: redoServiceCode.r,
      assignedTreatmentIds: redoServiceCode.t,
      checkoutTreeNodeId: redoServiceCode.n,
      documentId: redoServiceCode.d,
    };
  } catch {
    return null;
  }
}

/**
 * Lean equivalent of `parseShippingMethodCode` from ./service-code.ts.
 *
 * The parse-attempt ORDER (compact → dynamic → fixed) is load-bearing and
 * mirrors the canonical parser exactly — see the warning comment on the
 * canonical function. Do not reorder.
 */
export function parseShippingMethodCode(
  code?: string | null,
):
  | {
      parsedInfo: NonNullable<
        ReturnType<typeof parseDynamicShippingMethodCode>
      >;
      type: "legacy-dynamic";
    }
  | {
      parsedInfo: NonNullable<ReturnType<typeof parseFixedShippingMethodCode>>;
      type: "fixed";
    }
  | { parsedInfo: NonNullable<CompactServiceCodeParsedInfo>; type: "compact" }
  | null {
  if (!code) {
    return null;
  }

  const compact = parseCompactServiceCode(code);
  if (compact) {
    return { parsedInfo: compact, type: "compact" };
  }

  const dynamic = parseDynamicShippingMethodCode(code);
  if (dynamic) {
    return { parsedInfo: dynamic, type: "legacy-dynamic" };
  }

  const fixed = parseFixedShippingMethodCode(code);
  if (fixed) {
    return { parsedInfo: fixed, type: "fixed" };
  }

  return null;
}
